import * as React from "react";
import { Autocomplete } from "@material-ui/lab";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import { Popper, List } from "@material-ui/core";
import styled from "styled-components";
import { SearchResult } from "src/services/search";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { SearchInputField } from "./SearchInputField";
import content from "../../cms/searchbar.json";

interface ISearchBarProps {
  loading: boolean;
  open: boolean;
  onChange: (event, value, release) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onEnter: () => void;
  results: SearchResult[];
  query: string;
}

const StyledPopper = styled(Popper)`
  height: auto;
`;

const StyledList = styled(List)`
  box-shadow: 0 7px 10px 0 rgba(0, 0, 0, 0.08);
`;

const useStyles = makeStyles((theme) => ({
  SearchResultList: {
    background: theme.palette.background.paper,
    "& ul.MuiAutocomplete-listbox": {
      padding: 0,
    },
    "& div.MuiAutocomplete-noOptions": {
      background: theme.palette.grey[100],
    },
    "& .MuiAutocomplete-loading": {
      display: "none",
    },
  },
  AutoCompleteField: {
    background: theme.palette.background.paper,
    borderRadius: "4px 4px 0 0",
  },
}));

const renderSearchInputField = (params, onBlur, loading): JSX.Element => (
  <SearchInputField params={params} onBlur={onBlur} loading={loading} />
);

const HIGHLIGHTED = 700; //highlighted part of the the option in dropdown
const STANDARD = 400;
const ENTER_KEY = "Enter";

const renderOption = (option, { inputValue }): JSX.Element => {
  const matches = match(option, inputValue);
  const parts = parse(option, matches);
  return (
    <div>
      {parts.map((part, index) => (
        <span
          key={index}
          style={{ fontWeight: part.highlight ? HIGHLIGHTED : STANDARD }}
        >
          {part.text}
        </span>
      ))}
    </div>
  );
};

const renderSearchResultList = (props, classes): JSX.Element => (
  <StyledList
    {...props}
    classes={{ root: classes.SearchResultList }}
    className="search-result-list"
    disablePadding={true}
  />
);

export const SearchBar = ({
  loading,
  open,
  onChange,
  onBlur,
  onEnter,
  results,
  query,
}: ISearchBarProps): JSX.Element => {
  const classes = useStyles({});

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ENTER_KEY) {
      onEnter();
    }
  };

  return (
    <Autocomplete
      id="search-autocomplete"
      open={open}
      autoHighlight={true}
      classes={{ root: classes.AutoCompleteField }}
      getOptionSelected={() => false}
      inputValue={query}
      disableClearable={false}
      options={results.map((option) => option.text)}
      filterOptions={(options) => options}
      onInputChange={onChange}
      loading={loading}
      loadingText={""}
      renderInput={(params) => renderSearchInputField(params, onBlur, loading)}
      renderOption={renderOption}
      PopperComponent={StyledPopper}
      PaperComponent={(props) => renderSearchResultList(props, classes)}
      onKeyDown={onKeyDown}
      noOptionsText={content.noOptionsText}
    />
  );
};
